import React from "react";
import { CalendarIcon, Info } from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "components/ui/tooltip";
import { format } from "date-fns";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "components/ui/popover";
import { Button } from "components/ui/button";
import { Calendar } from "components/ui/calendar";
import FuelPriceChart from "./FuelPriceChart";
import FuelStatsCard from "./FuelStatsCard";
import type { FuelCityAnalyticsResponse } from "../api";

interface Props {
    data: FuelCityAnalyticsResponse | null;
    loading: boolean;
    error?: string;
    dateRange: "1W" | "1M" | "3M" | "CUSTOM";
    onDateRangeChange: (range: "1W" | "1M" | "3M" | "CUSTOM") => void;
    fromDate: string;
    toDate: string;
    onFromDateChange: (value: string) => void;
    onToDateChange: (value: string) => void;
}

const FuelAnalytics: React.FC<Props> = ({
    data,
    loading,
    error,
    dateRange,
    onDateRangeChange,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
}) => {
    const pills = ["1W", "1M", "3M", "CUSTOM"] as const;
    const DATA_START_DATE = new Date(2026, 4, 20); // Month is 0-based, so 4 = May
    const TODAY = new Date();

    const isPillDisabled = (pill: "1W" | "1M" | "3M" | "CUSTOM") => {
        if (pill === "CUSTOM") {
            return false;
        }

        const today = new Date();
        const from = new Date(today);

        if (pill === "1W") {
            from.setDate(today.getDate() - 6);
        }

        if (pill === "1M") {
            from.setMonth(today.getMonth() - 1);
        }

        if (pill === "3M") {
            from.setMonth(today.getMonth() - 3);
        }

        return from < DATA_START_DATE;
    };

    const fromDateObj = fromDate ? new Date(fromDate) : undefined;
    const toDateObj = toDate ? new Date(toDate) : undefined;

    const summary = data?.summary ?? null;
    const changeValue = summary?.Increase ?? 0;

    const isPriceIncrease = changeValue > 0;

    // Fuel price increasing = RED
    // Fuel price decreasing = GREEN
    const graphColor = isPriceIncrease ? "#dc2626" : "#16a34a";

    if (loading) {
        return (
            <div className="h-full rounded-2xl border bg-white overflow-hidden">
                <div className="p-6 space-y-6 animate-pulse">
                    <div className="flex items-start justify-between">
                        <div className="space-y-3">
                            <div className="h-8 w-56 rounded bg-gray-200" />
                            <div className="h-10 w-40 rounded bg-gray-200" />
                        </div>

                        <div className="h-10 w-72 rounded bg-gray-200" />
                    </div>

                    <div className="flex gap-2">
                        {Array.from({ length: 4 }).map((_, idx) => (
                            <div
                                key={idx}
                                className="h-10 w-16 rounded-lg bg-gray-200"
                            />
                        ))}
                    </div>

                    <div className="h-[420px] rounded-xl bg-gray-200" />

                    <div className="grid grid-cols-4 gap-4">
                        {Array.from({ length: 8 }).map((_, idx) => (
                            <div
                                key={idx}
                                className="h-28 rounded-xl bg-gray-200"
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="h-full rounded-2xl border bg-white flex items-center justify-center text-gray-500 text-sm">
                No analytics available
            </div>
        );
    }

    return (
        <div className="h-full rounded-2xl border bg-white overflow-hidden flex flex-col">
            <div className="px-6 py-5 border-b shrink-0">
                <div className="flex items-start justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold tracking-tight">{summary?.City || "Analytics"}</h1>

                            {summary?.City?.toUpperCase() === "INDEX" && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Info className="h-4 w-4 text-gray-400 cursor-pointer" />
                                        </TooltipTrigger>

                                        <TooltipContent>
                                            <p className="max-w-xs text-xs">
                                                Index represents the average diesel price across New Delhi, Mumbai, Kolkata and Chennai.
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>

                        <div className="mt-1 text-sm text-gray-500 font-medium">Diesel</div>
                    </div>

                    <div className="flex flex-col items-center flex-1">
                        <div className="text-3xl font-bold tracking-tight">
                            ₹{summary?.CurrentRate?.toFixed(2) ?? "0.00"}
                        </div>

                        <div className={`mt-1 flex items-center gap-3 text-sm font-semibold ${isPriceIncrease ? "text-red-600" : "text-green-600"}`}>
                            <span>
                                {isPriceIncrease ? "+" : ""}
                                ₹{Math.abs(summary?.Increase ?? 0).toFixed(2)}
                            </span>

                            <span>today</span>

                            <span>
                                {isPriceIncrease ? "+" : ""}
                                {Math.abs(summary?.IncreasePercentage ?? 0).toFixed(2)}%
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="w-[135px] justify-start text-xs">
                                    <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                                    {fromDateObj ? format(fromDateObj, "dd MMM yyyy") : "From"}
                                </Button>
                            </PopoverTrigger>

                            <PopoverContent className="w-auto p-0" align="end">
                                <Calendar
                                    mode="single"
                                    selected={fromDateObj}
                                    disabled={(date) =>
                                        date < DATA_START_DATE ||
                                        date > TODAY ||
                                        (toDateObj ? date > toDateObj : false)
                                    }
                                    onSelect={(date) => {
                                        if (!date) return;

                                        onDateRangeChange("CUSTOM");
                                        onFromDateChange(format(date, "yyyy-MM-dd"));
                                    }}
                                />
                            </PopoverContent>
                        </Popover>

                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="w-[135px] justify-start text-xs">
                                    <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                                    {toDateObj ? format(toDateObj, "dd MMM yyyy") : "To"}
                                </Button>
                            </PopoverTrigger>

                            <PopoverContent
                                className="w-auto p-0"
                                align="end"
                            >
                                <Calendar
                                    mode="single"
                                    selected={toDateObj}
                                    disabled={(date) =>
                                        date < DATA_START_DATE ||
                                        date > TODAY ||
                                        (fromDateObj ? date < fromDateObj : false)
                                    }
                                    onSelect={(date) => {
                                        if (!date) return;

                                        onDateRangeChange("CUSTOM");
                                        onToDateChange(format(date, "yyyy-MM-dd"));
                                    }}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                <div className="flex items-center gap-2 mt-6">
                    {pills.map((pill) => {
                        const disabled = isPillDisabled(pill);

                        return (
                            <button
                                key={pill}
                                disabled={disabled}
                                onClick={() => {
                                    if (!disabled) {
                                        onDateRangeChange(pill);
                                    }
                                }}
                                className={`px-4 py-2 rounded-lg border text-xs font-medium transition-all
                                ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200" : dateRange === pill ? "bg-blue-600 border-blue-600 text-white" : "bg-white hover:bg-gray-50 text-gray-700"}`}
                            >
                                {pill}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
                {error || !summary ? (
                    <div className="h-full min-h-[400px] flex items-center justify-center">
                        <div className="text-center">
                            <div className="text-red-600 text-sm font-medium">
                                {error || data.message || "No analytics available"}
                            </div>

                            <div className="mt-2 text-xs text-gray-500">
                                Please change the date range
                                or select another city
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        <div className="h-[300px]">
                            <FuelPriceChart
                                data={data.graph_data}
                                color={graphColor}
                                minPrice={Math.min(
                                    summary.BaseRate,
                                    summary.CurrentRate,
                                    summary.LowestRate
                                )}
                                maxPrice={Math.max(
                                    summary.BaseRate,
                                    summary.CurrentRate,
                                    summary.HighestRate
                                )}
                            />
                        </div>

                        <div className="grid grid-cols-4 gap-4">
                            <FuelStatsCard
                                label="Base Rate (₹/Litre)"
                                value={summary.BaseRate.toFixed(2)}
                            />

                            <FuelStatsCard
                                label="Current Rate (₹/Litre)"
                                value={summary.CurrentRate.toFixed(2)}
                            />

                            <FuelStatsCard
                                label="Change (₹)"
                                value={`${isPriceIncrease ? "+" : "-"}${Math.abs(summary.Increase).toFixed(2)}`}
                                valueClassName={
                                    isPriceIncrease
                                        ? "text-red-600"
                                        : "text-green-600"
                                }
                            />

                            <FuelStatsCard
                                label="Change (%)"
                                value={`${isPriceIncrease ? "+" : "-"}${Math.abs(summary.IncreasePercentage).toFixed(2)}%`}
                                valueClassName={
                                    isPriceIncrease
                                        ? "text-red-600"
                                        : "text-green-600"
                                }
                            />
                        </div>

                        <div className="grid grid-cols-4 gap-4">
                            <FuelStatsCard
                                label="Highest Rate (₹/Litre)"
                                value={summary.HighestRate.toFixed(2)}
                            />

                            <FuelStatsCard
                                label="Lowest Rate (₹/Litre)"
                                value={summary.LowestRate.toFixed(2)}
                            />

                            <FuelStatsCard
                                label="Average Rate (₹/Litre)"
                                value={summary.AverageRate.toFixed(2)}
                            />

                            <FuelStatsCard
                                label="Largest Daily Move (₹)"
                                value={summary.LargestDailyMove.toFixed(2)}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FuelAnalytics;